import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { KeyRound, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Player profile section: set / change / clear the 4-digit Kiosk PIN. */
const KioskPinSection = () => {
    const [hasPin, setHasPin] = useState(null);            // null = loading, true/false = known
    const [editing, setEditing] = useState(false);
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const loadStatus = async () => {
        try {
            const { data } = await axios.get(`${API}/user/kiosk-pin`, { withCredentials: true });
            setHasPin(!!data?.has_pin);
        } catch {
            setHasPin(false);
        }
    };

    useEffect(() => { loadStatus(); }, []);

    const reset = () => {
        setEditing(false);
        setPin('');
        setConfirmPin('');
        setCurrentPassword('');
    };

    const handleSave = async () => {
        if (!/^\d{4}$/.test(pin)) {
            toast.error('PIN must be exactly 4 digits');
            return;
        }
        if (pin !== confirmPin) {
            toast.error('PINs do not match');
            return;
        }
        if (hasPin && !currentPassword) {
            toast.error('Enter your account password to change your PIN');
            return;
        }
        setSaving(true);
        try {
            const body = { pin };
            if (hasPin) body.current_password = currentPassword;
            await axios.post(`${API}/user/kiosk-pin`, body, { withCredentials: true });
            toast.success(hasPin ? 'Kiosk PIN updated' : 'Kiosk PIN saved');
            reset();
            await loadStatus();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save PIN');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async () => {
        if (!window.confirm('Remove your Kiosk PIN? You will not be able to load cash at the kiosk until you set a new one.')) return;
        setSaving(true);
        try {
            await axios.delete(`${API}/user/kiosk-pin`, { withCredentials: true });
            toast.success('Kiosk PIN removed');
            reset();
            await loadStatus();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to remove PIN');
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="card-dark p-6 mb-6"
            data-testid="kiosk-pin-section"
        >
            <div className="flex items-start gap-3 mb-4">
                <KeyRound className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-0.5" />
                <div>
                    <h3 className="text-lg font-bold text-white">Kiosk PIN <span className="text-[#A0AAB2] text-xs font-normal">· optional</span></h3>
                    <p className="text-[#A0AAB2] text-sm mt-1">
                        Only needed if you plan to load cash at a BETohalics kiosk. You can keep loading the usual way (admin top-up, peer transfer, etc.) without ever setting one.
                    </p>
                </div>
            </div>

            {hasPin === null && (
                <p className="text-[#A0AAB2] text-sm">Loading…</p>
            )}

            {hasPin === false && !editing && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                    <ShieldAlert className="w-5 h-5 text-[#A0AAB2]" />
                    <p className="text-[#A0AAB2] text-sm flex-1">No PIN set. Set one only if you'll use a kiosk to load cash.</p>
                </div>
            )}

            {hasPin === true && !editing && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#39FF14]/10 border border-[#39FF14]/30">
                    <ShieldCheck className="w-5 h-5 text-[#39FF14]" />
                    <p className="text-[#39FF14] text-sm flex-1">Your kiosk PIN is active.</p>
                </div>
            )}

            {!editing && hasPin !== null && (
                <div className="flex gap-2 mt-4">
                    <Button
                        onClick={() => setEditing(true)}
                        className="flex-1 bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold"
                        data-testid="kiosk-pin-edit-btn"
                    >
                        {hasPin ? 'Change PIN' : 'Set Kiosk PIN'}
                    </Button>
                    {hasPin && (
                        <Button
                            onClick={handleClear}
                            disabled={saving}
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
                            data-testid="kiosk-pin-clear-btn"
                        >
                            Remove
                        </Button>
                    )}
                </div>
            )}

            {editing && (
                <div className="space-y-4 mt-4">
                    {hasPin && (
                        <div>
                            <Label className="text-white text-sm">Account Password</Label>
                            <Input
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="Your sign-in password"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="kiosk-pin-current-password"
                                autoComplete="current-password"
                            />
                        </div>
                    )}
                    <div>
                        <Label className="text-white text-sm">New 4-digit PIN</Label>
                        <Input
                            type="password"
                            inputMode="numeric"
                            pattern="\d{4}"
                            maxLength={4}
                            value={pin}
                            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            placeholder="••••"
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white text-2xl font-mono tracking-[0.5em] text-center"
                            data-testid="kiosk-pin-new"
                            autoComplete="new-password"
                        />
                    </div>
                    <div>
                        <Label className="text-white text-sm">Confirm PIN</Label>
                        <Input
                            type="password"
                            inputMode="numeric"
                            pattern="\d{4}"
                            maxLength={4}
                            value={confirmPin}
                            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            placeholder="••••"
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white text-2xl font-mono tracking-[0.5em] text-center"
                            data-testid="kiosk-pin-confirm"
                            autoComplete="new-password"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={handleSave}
                            disabled={saving || pin.length !== 4 || confirmPin.length !== 4}
                            className="flex-1 bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold"
                            data-testid="kiosk-pin-save"
                        >
                            {saving ? 'Saving…' : 'Save PIN'}
                        </Button>
                        <Button
                            onClick={reset}
                            disabled={saving}
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                            data-testid="kiosk-pin-cancel"
                        >
                            Cancel
                        </Button>
                    </div>
                    <p className="text-[11px] text-[#A0AAB2]">
                        Choose 4 digits you'll remember but a stranger couldn't guess. Avoid 1234, 0000, your birth year, or your phone's last 4 digits.
                    </p>
                </div>
            )}
        </motion.div>
    );
};

export default KioskPinSection;
